import { Loader2, MailIcon, PrinterIcon, Send as SendIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import type {
  SendFollowupSchema,
  TreatmentRecordRequestChannelSchema,
  TreatmentRecordRequestSchema,
} from '@/services/api/generated/treatmentRecordRequestsService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';

import { HipaaPackageStatus } from './HipaaPackageStatus';
import { useSendFollowupFlow } from './hooks/useSendFollowupFlow';
import { InlineChannelUpdateForm } from './InlineChannelUpdateForm';
import { InlineHipaaUploadForm } from './InlineHipaaUploadForm';

interface SendFollowupModalProps {
  isOpen: boolean;
  onClose: () => void;
  matterId: string;
  providerId: string;
  request: TreatmentRecordRequestSchema;
  /** z-index override for the dialog (use when rendered inside another modal) */
  zIndex?: number;
  /** Whether this dialog is nested inside another modal */
  nested?: boolean;
}

type ChannelType = 'fax' | 'email';

interface ChannelOption {
  type: ChannelType;
  label: string;
  value: string | null;
  icon: React.ReactNode;
  available: boolean;
}

const getChannelOptions = (
  channel: TreatmentRecordRequestChannelSchema | null | undefined,
): ChannelOption[] => {
  return [
    {
      type: 'fax',
      label: 'Fax',
      value: channel?.fax || null,
      icon: <PrinterIcon className="h-5 w-5" />,
      available: Boolean(channel?.fax),
    },
    {
      type: 'email',
      label: 'Email',
      value: channel?.email || null,
      icon: <MailIcon className="h-5 w-5" />,
      available: Boolean(channel?.email),
    },
  ];
};

const getDefaultChannel = (
  channel: TreatmentRecordRequestChannelSchema | null | undefined,
): ChannelType | null => {
  if (channel?.type === 'fax' && channel?.fax) return 'fax';
  if (channel?.type === 'email' && channel?.email) return 'email';

  // Fallback to first available
  if (channel?.fax) return 'fax';
  if (channel?.email) return 'email';

  return null;
};

export const SendFollowupModal = ({
  isOpen,
  onClose,
  matterId,
  providerId,
  request,
  zIndex,
  nested,
}: SendFollowupModalProps) => {
  const [selectedChannel, setSelectedChannel] = useState<ChannelType | null>(
    null,
  );
  const [coverMessage, setCoverMessage] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');

  const [sendFollowup, { isLoading: isSending }] =
    treatmentRecordRequestService.useSendFollowupTransmissionMutation();

  // Hook fetches channel data internally - auto-updates when mutations invalidate cache
  const {
    hasUsableHipaaPdf,
    isLoadingPackage,
    isLoadingChannel,
    isChannelMissingContact,
    parseApiError,
    setMissingRequirement,
    resetState,
    packageData,
    channelData,
  } = useSendFollowupFlow({
    matterId,
    providerId,
    requestId: request.id,
  });

  // Derive channel options from fetched channel data
  const channelOptions = getChannelOptions(channelData);

  // Check if selected channel is missing contact info
  const selectedChannelMissingContact =
    selectedChannel && isChannelMissingContact(selectedChannel, channelData);

  // Determine if we need to show inline forms
  const showHipaaUploadForm = !hasUsableHipaaPdf && !isLoadingPackage;
  // Show channel update form whenever selected channel is missing contact
  const showChannelUpdateForm = selectedChannelMissingContact;

  // Can send when:
  // - Has HIPAA PDF
  // - Has selected channel with contact info
  // - Not currently loading/sending
  const canSend =
    hasUsableHipaaPdf &&
    selectedChannel &&
    !selectedChannelMissingContact &&
    !isSending &&
    !isLoadingPackage &&
    !isLoadingChannel;

  // Set default channel when channelData loads or changes
  useEffect(() => {
    if (isOpen && channelData !== undefined) {
      setSelectedChannel(getDefaultChannel(channelData));
    }
  }, [channelData, isOpen]);

  const handleSend = useCallback(async () => {
    if (!selectedChannel) {
      toast.error('Please select a channel');
      return;
    }

    if (!hasUsableHipaaPdf) {
      setMissingRequirement('hipaa_pdf');
      toast.error('Please upload a HIPAA package first');
      return;
    }

    if (selectedChannelMissingContact) {
      setMissingRequirement('channel_contact');
      toast.error(
        `Please add a ${selectedChannel === 'fax' ? 'fax number' : 'email address'} first`,
      );
      return;
    }

    const payload: SendFollowupSchema = {
      channel: selectedChannel,
    };

    if (selectedChannel === 'fax' && coverMessage.trim()) {
      payload.cover_message = coverMessage.trim();
    }

    if (selectedChannel === 'email') {
      if (emailSubject.trim()) {
        payload.email_subject = emailSubject.trim();
      }
      if (emailBody.trim()) {
        payload.email_body = emailBody.trim();
      }
    }

    try {
      await sendFollowup({
        matterId,
        providerId,
        requestId: request.id,
        sendFollowupSchema: payload,
      }).unwrap();

      toast.success('Follow-up sent successfully');
      onClose();

      // Reset form
      setCoverMessage('');
      setEmailSubject('');
      setEmailBody('');
      resetState();
    } catch (error) {
      console.error('Failed to send follow-up:', error);

      // Parse error to determine what's missing
      const missingRequirement = parseApiError(error);
      if (missingRequirement !== 'none') {
        setMissingRequirement(missingRequirement);
        if (missingRequirement === 'hipaa_pdf') {
          toast.error('HIPAA package is required. Please upload one.');
        } else if (missingRequirement === 'channel_contact') {
          toast.error(
            `${selectedChannel === 'fax' ? 'Fax number' : 'Email address'} is required.`,
          );
        }
      } else {
        const errorObj = error as {
          data?: { error?: string; detail?: string };
        };
        const serverMessage =
          errorObj?.data?.error || errorObj?.data?.detail || null;
        toast.error(
          serverMessage || 'Failed to send follow-up. Please try again.',
        );
      }
    }
  }, [
    selectedChannel,
    hasUsableHipaaPdf,
    selectedChannelMissingContact,
    coverMessage,
    emailSubject,
    emailBody,
    sendFollowup,
    matterId,
    providerId,
    request.id,
    onClose,
    parseApiError,
    setMissingRequirement,
    resetState,
  ]);

  // Reset form when modal closes
  const handleClose = useCallback(() => {
    setSelectedChannel(getDefaultChannel(channelData));
    setCoverMessage('');
    setEmailSubject('');
    setEmailBody('');
    resetState();
    onClose();
  }, [channelData, onClose, resetState]);

  const handleHipaaUploadSuccess = useCallback(() => {
    // HIPAA package uploaded, state will refresh via RTK Query
  }, []);

  const handleChannelUpdateSuccess = useCallback(() => {
    // Channel updated, state will refresh via RTK Query invalidation
  }, []);

  const handleChannelSelect = useCallback((channelType: ChannelType) => {
    setSelectedChannel(channelType);
  }, []);

  const isLoading = isSending || isLoadingPackage || isLoadingChannel;

  return (
    <Dialog open={isOpen} onClose={handleClose} size="lg" zIndex={zIndex} nested={nested}>
      <DialogBody>
        <DialogTitle>Send Follow-up</DialogTitle>
        <DialogDescription>
          Resend the HIPAA package to follow up on your records request.
        </DialogDescription>

        <div className="mt-6 space-y-6">
          {/* HIPAA Package Upload Section - shown when no PDF exists */}
          {showHipaaUploadForm && (
            <InlineHipaaUploadForm
              matterId={matterId}
              providerId={providerId}
              requestId={request.id}
              onSuccess={handleHipaaUploadSuccess}
            />
          )}

          {/* Loading state for package check */}
          {isLoadingPackage && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-zinc-400 mr-2" />
              <span className="text-sm text-zinc-500">
                Checking HIPAA package...
              </span>
            </div>
          )}

          {/* Channel selection */}
          <fieldset>
            <legend className="text-sm font-medium text-zinc-700 mb-3 block">
              Channel
            </legend>
            <div className="space-y-2">
              {channelOptions.map((option) => (
                <label
                  key={option.type}
                  htmlFor={`channel-${option.type}`}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedChannel === option.type
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-zinc-200 hover:border-zinc-300'
                  }`}
                >
                  <input
                    id={`channel-${option.type}`}
                    type="radio"
                    name="channel"
                    value={option.type}
                    checked={selectedChannel === option.type}
                    onChange={() => handleChannelSelect(option.type)}
                    className="sr-only"
                  />
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-full ${
                      selectedChannel === option.type
                        ? 'bg-indigo-100 text-indigo-600'
                        : 'bg-zinc-100 text-zinc-500'
                    }`}
                  >
                    {option.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span
                        className={`font-medium ${
                          selectedChannel === option.type
                            ? 'text-indigo-900'
                            : 'text-zinc-900'
                        }`}
                      >
                        {option.label}
                      </span>
                      {selectedChannel === option.type && (
                        <span className="text-xs text-indigo-600 font-medium">
                          Selected
                        </span>
                      )}
                    </div>
                    {option.value ? (
                      <p className="text-sm text-zinc-500 truncate">
                        {option.value}
                      </p>
                    ) : (
                      <p className="text-sm text-zinc-400 italic">
                        Not configured
                      </p>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </fieldset>

          {/* Inline channel update form - shown when selected channel is missing contact */}
          {showChannelUpdateForm && selectedChannel && (
            <InlineChannelUpdateForm
              matterId={matterId}
              providerId={providerId}
              requestId={request.id}
              channelType={selectedChannel}
              currentChannel={channelData}
              onSuccess={handleChannelUpdateSuccess}
            />
          )}

          {/* Fax cover message */}
          {selectedChannel === 'fax' && !selectedChannelMissingContact && (
            <div className="block">
              <label
                htmlFor="cover-message"
                className="text-sm font-medium text-zinc-700 mb-2 block"
              >
                Cover Message{' '}
                <span className="text-zinc-400 font-normal">(optional)</span>
              </label>
              <textarea
                id="cover-message"
                value={coverMessage}
                onChange={(e) => setCoverMessage(e.target.value)}
                placeholder="Follow-up request for medical records. Original request sent on..."
                rows={3}
                className="w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 resize-none"
              />
            </div>
          )}

          {/* Email fields */}
          {selectedChannel === 'email' && !selectedChannelMissingContact && (
            <>
              <div className="block">
                <label
                  htmlFor="email-subject"
                  className="text-sm font-medium text-zinc-700 mb-2 block"
                >
                  Email Subject{' '}
                  <span className="text-zinc-400 font-normal">(optional)</span>
                </label>
                <input
                  id="email-subject"
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Follow-up: Medical Records Request"
                  className="w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <div className="block">
                <label
                  htmlFor="email-body"
                  className="text-sm font-medium text-zinc-700 mb-2 block"
                >
                  Email Body{' '}
                  <span className="text-zinc-400 font-normal">(optional)</span>
                </label>
                <textarea
                  id="email-body"
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  placeholder="This is a follow-up to our previous records request..."
                  rows={4}
                  className="w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 resize-none"
                />
              </div>
            </>
          )}

          {/* Package status - show when we have a valid package */}
          {hasUsableHipaaPdf && packageData && (
            <HipaaPackageStatus packageData={packageData} />
          )}
        </div>
      </DialogBody>

      <DialogActions>
        <Button outline onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button color="indigo" onClick={handleSend} disabled={!canSend}>
          {isSending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Sending...
            </>
          ) : (
            <>
              <SendIcon className="h-4 w-4 mr-2" />
              Send Follow-up
            </>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
